import { useState } from 'react'
import { Bell, ListPlus, Plus, Trash2, Workflow, X } from 'lucide-react'

import { DEAL_STAGES, STAGE_LABELS } from '@/types/deal'
import {
  ENROLLMENT_STATUS_LABELS,
  ENROLLMENT_STATUS_VARIANT,
  SEQUENCE_TRIGGER_LABELS,
  TEMPLATE_TRIGGER_LABELS,
  type CampaignChannel,
  type CustomerInterest,
  type Sequence,
  type SequenceTrigger,
  type TemplateTrigger,
} from '@/types/marketing'
import {
  useAudience,
  useCampaignTemplates,
  useCreateInterest,
  useCreateSequence,
  useDeleteInterest,
  useDeleteSequence,
  useEnrollCustomer,
  useInterests,
  useSequenceEnrollments,
  useSequences,
  useUpdateSequence,
} from '@/features/marketing/hooks'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

function interestSummary(i: CustomerInterest): string {
  const parts: string[] = []
  if (i.make) parts.push(i.make)
  if (i.condition) parts.push(i.condition)
  if (i.max_price_cents) parts.push(`≤ $${(i.max_price_cents / 100).toLocaleString()}`)
  return parts.length > 0 ? parts.join(' · ') : 'anything'
}

export function AutomationTab() {
  return (
    <div className="space-y-6">
      <InventoryAlertsCard />
      <SequencesCard />
    </div>
  )
}

// --- Inventory alerts (interests + bound alert templates) ---

function InventoryAlertsCard() {
  const { data: interests = [], isLoading } = useInterests()
  const { data: templates = [] } = useCampaignTemplates()
  const deleteMutation = useDeleteInterest()
  const [addOpen, setAddOpen] = useState(false)

  const alertTemplates = templates.filter((t) => t.trigger_type)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" /> Inventory alerts
            </CardTitle>
            <CardDescription>
              When a listing matches a customer's interest (make, condition, price ceiling) — on
              arrival or on a price drop — an alert campaign sends automatically using the bound
              template.
            </CardDescription>
          </div>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add interest
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {alertTemplates.length > 0 ? (
            alertTemplates.map((t) => (
              <Badge key={t.id} variant="secondary">
                {TEMPLATE_TRIGGER_LABELS[t.trigger_type as TemplateTrigger]} → “{t.name}”
              </Badge>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">
              No alert template bound yet — create a template in the Templates tab with an auto-use
              trigger.
            </span>
          )}
        </div>

        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : interests.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No interests recorded yet.
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Looking for</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {interests.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-medium">{i.customer_name || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {interestSummary(i)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteMutation.mutate(i.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <InterestDialog open={addOpen} onOpenChange={setAddOpen} />
    </Card>
  )
}

function InterestDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { data: audience = [] } = useAudience()
  const createMutation = useCreateInterest()

  const [customerId, setCustomerId] = useState('')
  const [make, setMake] = useState('')
  const [condition, setCondition] = useState('')
  const [maxPrice, setMaxPrice] = useState('')

  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (open) {
      setCustomerId('')
      setMake('')
      setCondition('')
      setMaxPrice('')
    }
  }

  const submit = async () => {
    if (!customerId) return
    try {
      await createMutation.mutateAsync({
        customer_id: customerId,
        make: make.trim() || undefined,
        condition: condition || undefined,
        max_price_cents: maxPrice ? Math.round((Number(maxPrice) || 0) * 100) : undefined,
      })
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Customer interest</DialogTitle>
          <DialogDescription>
            A listing matching any of a customer's interest rows triggers an alert campaign.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Customer</label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger>
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                {audience.map((a) => (
                  <SelectItem key={a.customer_id} value={a.customer_id}>
                    {[a.first_name, a.last_name].filter(Boolean).join(' ') || a.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Make</label>
              <Input
                placeholder="Sailfish"
                value={make}
                onChange={(e) => setMake(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Condition</label>
              <Select
                value={condition || 'any'}
                onValueChange={(v) => setCondition(v === 'any' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="used">Used</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Max price</label>
              <Input
                type="number"
                min="0"
                placeholder="150,000"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!customerId || createMutation.isPending}>
            Add interest
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Nurture sequences ---

interface StepForm {
  offset_days: string
  template_id: string
}

function SequencesCard() {
  const { data: sequences = [], isLoading } = useSequences()
  const { data: templates = [] } = useCampaignTemplates()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Sequence | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Workflow className="h-5 w-5" /> Nurture sequences
            </CardTitle>
            <CardDescription>
              Timed follow-ups per customer. Enroll on customer creation or a deal stage; stop
              automatically when a deal reaches the stop stage. Each step sends as its own campaign.
            </CardDescription>
          </div>
          <Button
            onClick={() => {
              setEditing(null)
              setDialogOpen(true)
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> New Sequence
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : sequences.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No sequences yet.
          </div>
        ) : (
          <div className="space-y-3">
            {sequences.map((s) => (
              <SequenceRow
                key={s.id}
                sequence={s}
                templates={templates}
                expanded={expanded === s.id}
                onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                onEdit={() => {
                  setEditing(s)
                  setDialogOpen(true)
                }}
              />
            ))}
          </div>
        )}
      </CardContent>

      <SequenceDialog open={dialogOpen} onOpenChange={setDialogOpen} sequence={editing} />
    </Card>
  )
}

function SequenceRow({
  sequence,
  templates,
  expanded,
  onToggle,
  onEdit,
}: {
  sequence: Sequence
  templates: { id: string; name: string }[]
  expanded: boolean
  onToggle: () => void
  onEdit: () => void
}) {
  const deleteMutation = useDeleteSequence()
  const enrollMutation = useEnrollCustomer()
  const { data: audience = [] } = useAudience()
  const { data: enrollments = [] } = useSequenceEnrollments(sequence.id)
  const [enrollCustomerId, setEnrollCustomerId] = useState('')

  const templateName = (id: string) =>
    templates.find((t) => t.id === id)?.name ?? 'missing template'

  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <div className="font-medium">{sequence.name}</div>
          <div className="text-xs text-muted-foreground">
            {SEQUENCE_TRIGGER_LABELS[sequence.enroll_trigger]}
            {sequence.enroll_trigger === 'deal_stage' && sequence.enroll_stage
              ? `: ${STAGE_LABELS[sequence.enroll_stage as keyof typeof STAGE_LABELS]}`
              : ''}
            {sequence.stop_stage &&
              ` · stops at ${STAGE_LABELS[sequence.stop_stage as keyof typeof STAGE_LABELS]}`}
            {` · ${sequence.steps.length} step${sequence.steps.length === 1 ? '' : 's'}`}
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {sequence.steps.map((step) => (
              <Badge key={step.id} variant="outline">
                day {step.offset_days} · {templateName(step.template_id)}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" title="Enrollments" onClick={onToggle}>
            <ListPlus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title="Delete"
            onClick={() => deleteMutation.mutate(sequence.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-3 border-t p-4">
          {enrollments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No enrollments yet.</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Next step</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrollments.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">{e.customer_name}</TableCell>
                      <TableCell>
                        <Badge variant={ENROLLMENT_STATUS_VARIANT[e.status]}>
                          {ENROLLMENT_STATUS_LABELS[e.status]}
                        </Badge>
                        {e.stopped_reason && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {e.stopped_reason}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        step {Math.min(e.current_step_index + 1, sequence.steps.length)} /{' '}
                        {sequence.steps.length}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {e.next_step_at ? new Date(e.next_step_at).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Select value={enrollCustomerId} onValueChange={setEnrollCustomerId}>
              <SelectTrigger className="max-w-xs">
                <SelectValue placeholder="Enroll a customer manually…" />
              </SelectTrigger>
              <SelectContent>
                {audience.map((a) => (
                  <SelectItem key={a.customer_id} value={a.customer_id}>
                    {[a.first_name, a.last_name].filter(Boolean).join(' ') || a.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              disabled={!enrollCustomerId || enrollMutation.isPending}
              onClick={async () => {
                await enrollMutation.mutateAsync({
                  sequenceId: sequence.id,
                  customerId: enrollCustomerId,
                })
                setEnrollCustomerId('')
              }}
            >
              Enroll
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function SequenceDialog({
  open,
  onOpenChange,
  sequence,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  sequence: Sequence | null
}) {
  const { data: templates = [] } = useCampaignTemplates()
  const createMutation = useCreateSequence()
  const updateMutation = useUpdateSequence()
  const pending = createMutation.isPending || updateMutation.isPending

  const [name, setName] = useState('')
  const [enrollTrigger, setEnrollTrigger] = useState<SequenceTrigger>('customer_created')
  const [enrollStage, setEnrollStage] = useState('')
  const [stopStage, setStopStage] = useState('')
  const [steps, setSteps] = useState<StepForm[]>([])

  const formKey = `${open}:${sequence?.id ?? ''}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open) {
      if (sequence) {
        setName(sequence.name)
        setEnrollTrigger(sequence.enroll_trigger)
        setEnrollStage(sequence.enroll_stage)
        setStopStage(sequence.stop_stage)
        setSteps(
          sequence.steps.map((s) => ({
            offset_days: String(s.offset_days),
            template_id: s.template_id,
          })),
        )
      } else {
        setName('')
        setEnrollTrigger('customer_created')
        setEnrollStage('')
        setStopStage('')
        setSteps([{ offset_days: '0', template_id: '' }])
      }
    }
  }

  const canSave =
    name.trim().length > 0 &&
    steps.length > 0 &&
    steps.every((s) => s.template_id && Number(s.offset_days) >= 0)

  const submit = async () => {
    if (!canSave) return
    const body = {
      name: name.trim(),
      enroll_trigger: enrollTrigger,
      enroll_stage: enrollTrigger === 'deal_stage' ? enrollStage : undefined,
      stop_stage: stopStage || undefined,
      steps: steps.map((s) => ({
        offset_days: Number(s.offset_days) || 0,
        template_id: s.template_id,
        channel: 'email' as CampaignChannel,
      })),
    }
    try {
      if (sequence) await updateMutation.mutateAsync({ id: sequence.id, body })
      else await createMutation.mutateAsync(body)
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{sequence ? 'Edit Sequence' : 'New Sequence'}</DialogTitle>
          <DialogDescription>
            Steps send N days after enrollment, in order. Offsets must increase.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              placeholder="New lead nurture"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Enroll</label>
              <Select
                value={enrollTrigger}
                onValueChange={(v) => setEnrollTrigger(v as SequenceTrigger)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer_created">Customer created</SelectItem>
                  <SelectItem value="deal_stage">Deal enters stage</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {enrollTrigger === 'deal_stage' ? (
              <div className="space-y-2">
                <label className="text-sm font-medium">Enroll stage</label>
                <Select value={enrollStage} onValueChange={setEnrollStage}>
                  <SelectTrigger>
                    <SelectValue placeholder="Stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEAL_STAGES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STAGE_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium">Stop at stage</label>
                <Select
                  value={stopStage || 'never'}
                  onValueChange={(v) => setStopStage(v === 'never' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Run to completion</SelectItem>
                    {DEAL_STAGES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STAGE_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {enrollTrigger === 'deal_stage' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Stop at stage</label>
              <Select
                value={stopStage || 'never'}
                onValueChange={(v) => setStopStage(v === 'never' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Run to completion</SelectItem>
                  {DEAL_STAGES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STAGE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Steps</label>
            {steps.map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  className="w-24"
                  value={step.offset_days}
                  onChange={(e) =>
                    setSteps((s) =>
                      s.map((st, j) => (j === i ? { ...st, offset_days: e.target.value } : st)),
                    )
                  }
                />
                <span className="text-sm text-muted-foreground">days →</span>
                <Select
                  value={step.template_id}
                  onValueChange={(v) =>
                    setSteps((s) => s.map((st, j) => (j === i ? { ...st, template_id: v } : st)))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSteps((s) => s.filter((_, j) => j !== i))}
                  disabled={steps.length === 1}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setSteps((s) => [
                  ...s,
                  {
                    offset_days: String((Number(s[s.length - 1]?.offset_days) || 0) + 3),
                    template_id: '',
                  },
                ])
              }
            >
              <Plus className="mr-1 h-4 w-4" /> Add step
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !canSave}>
            {sequence ? 'Save Changes' : 'Create Sequence'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
